/* eslint-disable react-refresh/only-export-components */
import { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { fetchPolicy, login } from '../api/auth';
import type { AccessPolicy as AccessPolicyShape, SessionInfo, UserInfo } from '../types/auth';
import { AccessPolicy } from '../utils/accessPolicy';
import { clearInventoryOfflineContext } from '../utils/inventoryOfflineContext';
import { clearReceiptOfflineContexts } from '../utils/receiptOfflineDraft';
import { clearMerchantPortalSelection } from '../utils/merchantPortalSelection';
import { useThemePreference } from '../hooks/useThemePreference';

type ImpersonationState = {
  originalToken: string | null;
  impersonator: UserInfo;
};

type SessionPayload = {
  token: string;
  user: UserInfo;
  policy?: AccessPolicyShape | null;
  session?: SessionInfo | null;
  impersonation?: ImpersonationState | null;
};

type AuthState = {
  user: UserInfo | null;
  token: string | null;
  policy: AccessPolicy | null;
  session: SessionInfo | null;
  impersonation: ImpersonationState | null;
  isImpersonating: boolean;
  loading: boolean;
  login: (email: string, password: string) => Promise<AccessPolicy | null>;
  setSession: (session: SessionPayload) => void;
  startImpersonation: (session: SessionPayload) => void;
  stopImpersonation: () => void;
  logout: () => void;
  refreshPolicy: () => Promise<void>;
};

export const AuthContext = createContext<AuthState | undefined>(undefined);

const TOKEN_KEY = 'coop_token';
const IMPERSONATION_KEY = 'coop_impersonation';

const readStoredImpersonation = (): ImpersonationState | null => {
  try {
    if (typeof localStorage === 'undefined') return null;
    const raw = localStorage.getItem(IMPERSONATION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ImpersonationState;
    if (!parsed?.impersonator?.id || !parsed.impersonator.email) return null;
    return {
      originalToken: typeof parsed.originalToken === 'string' ? parsed.originalToken : null,
      impersonator: parsed.impersonator,
    };
  } catch {
    try {
      localStorage.removeItem(IMPERSONATION_KEY);
    } catch {
      // ignore
    }
    return null;
  }
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const { applyUserTheme } = useThemePreference();
  const [user, setUser] = useState<UserInfo | null>(null);
  const [token, setToken] = useState<string | null>(() => {
    try {
      return localStorage.getItem(TOKEN_KEY);
    } catch {
      return null;
    }
  });
  const [impersonation, setImpersonation] = useState<ImpersonationState | null>(() => readStoredImpersonation());
  const [loading, setLoading] = useState<boolean>(true);
  const [policy, setPolicy] = useState<AccessPolicy | null>(null);
  const [session, setSessionState] = useState<SessionInfo | null>(null);

  const handleLogout = useCallback(() => {
    clearInventoryOfflineContext();
    clearReceiptOfflineContexts();
    setUser(null);
    setToken(null);
    setPolicy(null);
    setSessionState(null);
    setImpersonation(null);
    clearMerchantPortalSelection();
    try {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(IMPERSONATION_KEY);
    } catch {
      // ignore
    }
  }, []);

  const applySession = useCallback((nextSession: SessionPayload) => {
    clearInventoryOfflineContext();
    clearReceiptOfflineContexts();
    setToken(nextSession.token);
    setUser(nextSession.user);
    applyUserTheme(nextSession.user.preferredTheme);
    setPolicy(new AccessPolicy(nextSession.policy));
    setSessionState(nextSession.session ?? null);
    setImpersonation(nextSession.impersonation ?? null);
    try {
      localStorage.setItem(TOKEN_KEY, nextSession.token);
      if (nextSession.impersonation) {
        localStorage.setItem(IMPERSONATION_KEY, JSON.stringify(nextSession.impersonation));
      } else {
        localStorage.removeItem(IMPERSONATION_KEY);
      }
    } catch {
      // ignore
    }
  }, [applyUserTheme]);

  const refreshPolicy = useCallback(async () => {
    if (!token) return;
    const me = await fetchPolicy(token);
    setUser(me.user);
    applyUserTheme(me.user.preferredTheme);
    setPolicy(new AccessPolicy(me.policy));
    setSessionState(me.session ?? null);
    const impersonator = me.session?.impersonator;
    if (impersonator) {
      setImpersonation((current) => current ?? { originalToken: null, impersonator });
    }
  }, [token, applyUserTheme]);

  const handleLogin = useCallback(
    async (email: string, password: string) => {
      const result = await login(email, password);
      applySession({ ...result, impersonation: null });
      return result.policy ? new AccessPolicy(result.policy) : null;
    },
    [applySession],
  );

  const startImpersonation = useCallback(
    (nextSession: SessionPayload) => {
      if (!token || !user) return;
      applySession({
        ...nextSession,
        impersonation: { originalToken: token, impersonator: user },
      });
    },
    [token, user, applySession],
  );

  const stopImpersonation = useCallback(() => {
    if (!impersonation?.originalToken) return;
    const originalToken = impersonation.originalToken;
    clearInventoryOfflineContext();
    clearReceiptOfflineContexts();
    setImpersonation(null);
    setToken(originalToken);
    try {
      localStorage.setItem(TOKEN_KEY, originalToken);
      localStorage.removeItem(IMPERSONATION_KEY);
    } catch {
      // ignore
    }
  }, [impersonation]);

  useEffect(() => {
    const bootstrap = async () => {
      if (!token) {
        setLoading(false);
        return;
      }
      try {
        // Session discovery must not navigate public guest/receipt/station routes.
        // On rejection, logout below clears stale credentials; protected route
        // guards still send unauthenticated operators to the normal login form.
        const me = await fetchPolicy(token, { redirectOnUnauthorized: false });
        setUser(me.user);
        applyUserTheme(me.user.preferredTheme);
        setPolicy(new AccessPolicy(me.policy));
        setSessionState(me.session ?? null);
        const storedImpersonation = readStoredImpersonation();
        if (storedImpersonation) {
          setImpersonation(storedImpersonation);
        } else if (me.session?.impersonator) {
          const impersonator = me.session.impersonator;
          setImpersonation({ originalToken: null, impersonator });
        } else {
          setImpersonation(null);
        }
      } catch {
        // token invalid/expired
        handleLogout();
      } finally {
        setLoading(false);
      }
    };
    bootstrap();
  }, [token, handleLogout, applyUserTheme]);

  const value = useMemo(
    () => ({
      user,
      token,
      policy,
      session,
      impersonation,
      isImpersonating: Boolean(impersonation),
      loading,
      login: handleLogin,
      setSession: applySession,
      startImpersonation,
      stopImpersonation,
      logout: handleLogout,
      refreshPolicy,
    }),
    [
      user,
      token,
      policy,
      session,
      impersonation,
      loading,
      handleLogin,
      applySession,
      startImpersonation,
      stopImpersonation,
      handleLogout,
      refreshPolicy,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
